import React, { useEffect } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { CheckCircle2, ArrowRight, Download, Mail } from 'lucide-react';
import Navbar from '../../components/Common/Navbar';
import Footer from '../../components/Common/Footer';
import { theme } from '../../styles/theme';

const c = theme.colors;

const CheckoutSuccess = () => {
  const { plan } = useParams();
  const [params] = useSearchParams();
  const ref = params.get('ref') || 'A1XDEMO';
  const amt = params.get('amt') || '0';

  useEffect(() => {
    // Confetti / scroll to top.
    window.scrollTo(0, 0);
  }, []);

  const styles = {
    page: { fontFamily: theme.font.family, color: c.body, backgroundColor: c.surfaceAlt, minHeight: '100vh' },
    hero: { background: theme.gradient.dark, color: '#fff', padding: '64px 24px 48px', textAlign: 'center' },
    iconWrap: {
      width: 80, height: 80, borderRadius: '50%',
      background: 'rgba(16,185,129,0.18)', border: '2px solid #10B981',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      marginBottom: 20, animation: 'pop .5s ease-out',
    },
    h1: { fontSize: 'clamp(1.6rem, 3vw, 2.25rem)', fontWeight: 800, letterSpacing: '-0.02em', margin: '0 0 8px' },
    sub: { color: 'rgba(255,255,255,0.78)', fontSize: '1rem' },

    receipt: {
      maxWidth: 560, margin: '-32px auto 40px', padding: '32px',
      background: c.surface, border: `1px solid ${c.border}`, borderRadius: theme.radius.lg,
      boxShadow: theme.shadow.md, position: 'relative', zIndex: 2,
    },
    receiptHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px', paddingBottom: '16px', borderBottom: `1px solid ${c.border}` },
    brand: { fontSize: '1.1rem', fontWeight: 800, color: c.ink, background: theme.gradient.primary, WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent' },
    badge: {
      display: 'inline-flex', alignItems: 'center', gap: '6px',
      padding: '6px 12px', background: '#D1FAE5', color: '#065F46',
      borderRadius: theme.radius.pill, fontSize: '0.78rem', fontWeight: 700,
    },
    line: { display: 'flex', justifyContent: 'space-between', padding: '8px 0', fontSize: '0.9rem', color: c.body },
    lineLabel: { color: c.muted },
    lineVal: { color: c.ink, fontWeight: 600 },
    amountBig: { fontSize: '2rem', fontWeight: 800, color: c.ink, marginTop: '8px' },
    actionRow: { display: 'flex', gap: '10px', marginTop: '24px', flexWrap: 'wrap' },
    primaryBtn: {
      flex: 1, minWidth: 180,
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
      padding: '13px 18px', background: theme.gradient.primary, color: '#fff',
      borderRadius: theme.radius.md, textDecoration: 'none', fontWeight: 700, fontSize: '0.95rem',
      boxShadow: theme.shadow.glow, border: 'none', cursor: 'pointer',
    },
    ghostBtn: {
      flex: 1, minWidth: 140,
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
      padding: '13px 18px', background: c.primarySoft, color: c.primary,
      border: `1px solid ${c.border}`, borderRadius: theme.radius.md,
      textDecoration: 'none', fontWeight: 600, fontSize: '0.95rem', cursor: 'pointer',
    },

    nextSteps: {
      maxWidth: 880, margin: '0 auto 80px', padding: '0 24px',
    },
    stepsTitle: { textAlign: 'center', fontSize: '1.25rem', fontWeight: 800, color: c.ink, marginBottom: '24px' },
    stepGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px' },
    stepCard: {
      background: c.surface, border: `1px solid ${c.border}`, borderRadius: theme.radius.md,
      padding: '20px', textAlign: 'center',
    },
    stepIcon: {
      width: 36, height: 36, borderRadius: theme.radius.sm,
      background: c.primarySoft, color: c.primary,
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: '12px', fontWeight: 800,
    },
    stepBody: { fontSize: '0.9rem', color: c.body, lineHeight: 1.5 },
  };

  return (
    <div style={styles.page}>
      <style>{`@keyframes pop{0%{transform:scale(0.5);opacity:0}60%{transform:scale(1.1)}100%{transform:scale(1);opacity:1}}`}</style>
      <Navbar />

      <section style={styles.hero}>
        <div style={styles.iconWrap}><CheckCircle2 size={42} color="#10B981" strokeWidth={2.4} /></div>
        <h1 style={styles.h1}>Payment successful</h1>
        <p style={styles.sub}>Aapka {plan ? plan.charAt(0).toUpperCase() + plan.slice(1) : ''} plan activate ho gaya hai. Receipt aapki email pe bhej di hai.</p>
      </section>

      <div style={styles.receipt}>
        <div style={styles.receiptHeader}>
          <div>
            <div style={styles.brand}>A1xPay</div>
            <div style={{ fontSize: '0.78rem', color: c.muted, marginTop: '4px' }}>Payment receipt</div>
          </div>
          <div style={styles.badge}><CheckCircle2 size={12} /> SUCCESS</div>
        </div>

        <div style={styles.line}><span style={styles.lineLabel}>Reference ID</span><span style={styles.lineVal}><code>{ref}</code></span></div>
        <div style={styles.line}><span style={styles.lineLabel}>Plan</span><span style={styles.lineVal}>{plan ? plan.charAt(0).toUpperCase() + plan.slice(1) : ''}</span></div>
        <div style={styles.line}><span style={styles.lineLabel}>Date</span><span style={styles.lineVal}>{new Date().toLocaleString('en-IN')}</span></div>
        <div style={styles.line}><span style={styles.lineLabel}>Method</span><span style={styles.lineVal}>UPI / Card</span></div>

        <div style={{ ...styles.line, marginTop: '12px', paddingTop: '12px', borderTop: `1px dashed ${c.border}` }}>
          <span style={styles.lineLabel}>Amount paid</span>
          <span style={styles.amountBig}>₹{Number(amt).toLocaleString('en-IN')}</span>
        </div>

        <div style={styles.actionRow}>
          <Link to="/register" style={styles.primaryBtn}>Continue to dashboard <ArrowRight size={16} /></Link>
          <button style={styles.ghostBtn} onClick={() => window.print()}><Download size={16} /> Download</button>
        </div>
      </div>

      <div style={styles.nextSteps}>
        <h3 style={styles.stepsTitle}>What's next?</h3>
        <div style={styles.stepGrid}>
          <div style={styles.stepCard}>
            <div style={styles.stepIcon}>1</div>
            <div style={styles.stepBody}><strong>Account create karein</strong><br />/register pe ja kar email + password set karein.</div>
          </div>
          <div style={styles.stepCard}>
            <div style={styles.stepIcon}>2</div>
            <div style={styles.stepBody}><strong>KYC complete karein</strong><br />PAN + Aadhaar + business proof upload karein.</div>
          </div>
          <div style={styles.stepCard}>
            <div style={styles.stepIcon}>3</div>
            <div style={styles.stepBody}><strong>Integration shuru karein</strong><br />API key milne ke baad checkout flow integrate karein.</div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default CheckoutSuccess;
